import { useEffect, useMemo, useRef, useState } from 'react';
import { observer } from 'mobx-react';
import {
  Drawer,
  Form,
  Input,
  InputNumber,
  Modal,
  Button,
  Space,
  Typography,
  Tooltip,
  message,
  Tag,
  Tabs,
  Alert,
  Spin,
  Select,
  Empty,
} from 'antd';
import {
  SaveOutlined,
  CloseOutlined,
  ExperimentOutlined,
  DownloadOutlined,
  ThunderboltOutlined,
  WarningOutlined,
  CheckCircleOutlined,
  PlusOutlined,
  DeleteOutlined,
} from '@ant-design/icons';
import { useStore } from '@/stores';
import { AgentPickerTrigger } from '@/components/AgentPickerDrawer';
import { ModelPickerDrawer, modelLine } from '@/components/ModelPickerDrawer';
import {
  EvaluationModelRole,
  getModelPlan,
  type AppliedModel,
  type EvaluationModelOption,
} from '@/api/scenarioIntelligence';
import { TagsInput } from '@/components/TagsInput';
import { useUnsavedExit } from '@/components/unsavedExit';
import { colors } from '@/theme/tokens';
import {
  upsertScenario,
  setScenarioTags,
  scenarioTags,
  draftScenario,
  getSimilarScenarios,
  getCoverageGaps,
  exportScenarioJson,
  getScenarioById,
  listScenarioCategories,
  upsertSaved,
  type ScenarioCategoryDto,
  type ScenarioTurnSpec,
  type ScenarioConversationAssertion,
  type SimilarScenarioRow,
  type CoverageGapRow,
} from '@/api/scenarios';

const { Text } = Typography;
const { TextArea } = Input;

// ─────────────────────────────────────────────────────────────────────────────
// ScenarioEditorDrawer — cloud-native, AI-assisted scenario authoring.
//
// Three-pane layout (left → right):
//   1. Form     — scenarioId, agentId, merchant/member, description, turns
//   2. Preview  — live canonical JSON the server will persist
//   3. Sidebar  — AI co-pilot prompt, similar scenarios (dedupe), coverage
//                 gaps, lint warnings
//
// Saves go directly to ScenarioIndex + ScenarioSnapshot via /Upsert.
// "Export to JSON" downloads the same canonical body for git commit when
// an engineer wants disk history. Per the cloud-native shift: DB is
// editorial truth, files are the optional export path.
//
// LOSSLESS (2026-11-01): the form shows four phrase lists per turn and two per rule, but a scenario carries more —
// required tool calls, number grounding, the protocol-template scope… (266 of 1,303 files). Each turn and rule keeps
// what it was loaded with (`source`) and a save sends all of it back; a phrase list whose text was not touched goes
// back exactly as loaded, because a phrase can hold a comma. Removing a turn or rule that holds such checks asks
// first. The server refuses any save that loses them otherwise (ScenarioHiddenChecks).
//
// STUDIO-FOUNDATION REVIEW (4 Nov 2026): an edit sends back the hash of the version it opened, so a save over someone
// else's newer save is refused as a conflict (the advanced editor checks the same hash); "New scenario" never overwrites
// an existing id; the stage shown is the stored one and is kept unless changed; the category is chosen, not inferred from
// the id; and every way out with unsaved edits asks first (useUnsavedExit).
// ─────────────────────────────────────────────────────────────────────────────

interface Props {
  open: boolean;
  /** When provided, drawer loads this scenario for edit. When null, opens blank for create. */
  scenarioId: string | null;
  /** Optional pre-selected agent — used when launching from an agent-scoped page. */
  defaultAgentId?: number;
  /** Domain to default new scenarios into (e.g., "oncology"). */
  defaultDomain?: string;
  onClose: () => void;
  onSaved?: (scenarioId: string) => void | Promise<void>;
  /** A tag edit saved on its own (the library refreshes; the drawer stays open). */
  onTagsSaved?: () => void;
}

export const ScenarioEditorDrawer = observer(function ScenarioEditorDrawer({
  open,
  scenarioId,
  defaultAgentId,
  defaultDomain = 'oncology',
  onClose,
  onSaved,
  onTagsSaved,
}: Props) {
  const { themeStore, merchantContextStore } = useStore();
  const isDark = themeStore.mode === 'dark';
  const merchantId = merchantContextStore.selectedMerchantId;

  const [form] = Form.useForm<EditorFormValues>();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [drafting, setDrafting] = useState(false);
  const [draftPrompt, setDraftPrompt] = useState('');
  const [draftRationale, setDraftRationale] = useState<string | null>(null);
  const [draftError, setDraftError] = useState<string | null>(null);
  // D-SI-9: the Scenario model. The plan names it; one draft may choose another for itself (never saved as the setting).
  const [scenarioModel, setScenarioModel] = useState<EvaluationModelOption | null>(null);
  const [scenarioModelProblem, setScenarioModelProblem] = useState<string | null>(null);
  const [draftModel, setDraftModel] = useState<EvaluationModelOption | null>(null);
  const [draftModelPickerOpen, setDraftModelPickerOpen] = useState(false);
  const [answeredBy, setAnsweredBy] = useState<AppliedModel | null>(null);

  // Tags are not part of the scenario body: an existing scenario's tags save on their own the moment they change, so
  // a tag edit never goes through "Save scenario" (which rewrites the body and drops fields this form does not show).
  // A new scenario's tags are saved right after it is created.
  const [tags, setTags] = useState<string[]>([]);
  const [tagsState, setTagsState] = useState<'idle' | 'saving' | 'saved'>('idle');

  // Scenario-level fields the form has no control for; carried through a save unchanged.
  const [extras, setExtras] = useState<BodyExtras>(NO_EXTRAS);
  // The person confirmed removing a turn or rule that held checks this form does not show.
  const [removedHiddenChecks, setRemovedHiddenChecks] = useState(false);

  // The version this form opened (null for a new scenario), and a save refused because someone saved since.
  const [baseHash, setBaseHash] = useState<string | null>(null);
  const [conflict, setConflict] = useState<string | null>(null);
  const [reload, setReload] = useState(0);
  const [categories, setCategories] = useState<ScenarioCategoryDto[]>([]);
  // What the form held when it opened, to tell unsaved edits from none.
  const pristine = useRef('');

  const [similar, setSimilar] = useState<SimilarScenarioRow[]>([]);
  const [coverage, setCoverage] = useState<CoverageGapRow[]>([]);
  const [coverageLoading, setCoverageLoading] = useState(false);

  // Local mirror of form values for the live preview + similarity calls.
  // Form.useWatch would also work, but bundling everything we render off
  // a single state object makes the JSON-preview memo trivial.
  const [values, setValues] = useState<EditorFormValues>(blankForm(defaultAgentId, defaultDomain));

  // Throttle the similarity request — typing in the description shouldn't
  // hammer the API. 600ms is the sweet spot: responsive without burning
  // calls per keystroke.
  const similarTimer = useRef<number | null>(null);

  // Review M3 (2026-11-01): one drawer instance serves every scenario, so a load can outlive the open it was for. Each
  // open, close or switch of scenario starts a new generation; a load result, error, loading flag or tag-save answer from
  // an earlier one is dropped. Without it, A opened, closed and B opened, with A answering last, filled the form with A —
  // and the body save then used A's id while the tag save used B's.
  const generation = useRef(0);

  // The plan's Scenario model for this practice, read each time the drawer opens; a per-draft choice ends with the drawer.
  useEffect(() => {
    if (!open) {
      setDraftModel(null);
      setAnsweredBy(null);
      return;
    }
    let cancelled = false;
    setScenarioModel(null);
    setScenarioModelProblem(null);
    if (merchantId == null) {
      setScenarioModelProblem('Choose a practice first: the Scenario model runs on the practice’s own registered deployments.');
      return;
    }
    getModelPlan(merchantId)
      .then((plan) => {
        if (cancelled) return;
        const role = plan.roles.find((r) => r.role === EvaluationModelRole.Scenario);
        setScenarioModel(role?.row ?? null);
        setScenarioModelProblem(role?.problem ?? null);
      })
      .catch((e: Error) => !cancelled && setScenarioModelProblem(e.message));
    return () => {
      cancelled = true;
    };
  }, [open, merchantId]);

  // ── Load existing scenario for edit, or reset form for new ────────────
  useEffect(() => {
    const mine = ++generation.current;
    if (!open) {
      setLoading(false);
      return;
    }
    setDraftRationale(null);
    setDraftError(null);
    setSimilar([]);
    setTags([]);
    setTagsState('idle');
    setExtras(NO_EXTRAS);
    setRemovedHiddenChecks(false);
    setConflict(null);
    setBaseHash(null);
    // The form's store as it opened: what onValuesChange reports from here on is compared with it.
    const opened = () => {
      const snap = form.getFieldsValue(true) as EditorFormValues;
      setValues(snap);
      pristine.current = JSON.stringify(snap);
    };
    if (scenarioId == null) {
      setLoading(false);
      form.setFieldsValue(blankForm(defaultAgentId, defaultDomain));
      opened();
      return;
    }
    setLoading(true);
    getScenarioById(scenarioId)
      .then((detail) => {
        if (mine !== generation.current) return;
        setTags(scenarioTags(detail.summary));
        const parsed = detail.parsed;
        setExtras({ infusionProtocolId: parsed.infusionProtocolId ?? 0, memberContactId: parsed.memberContactId ?? 0 });
        setBaseHash(detail.baseHash ?? null);
        const next: EditorFormValues = {
          domain: detail.summary.areaSlug || defaultDomain,
          scenarioId: parsed.scenarioId,
          agentId: parsed.agentId,
          merchantId: parsed.merchantId,
          memberId: parsed.memberId,
          description: parsed.description ?? '',
          // The stored stage (review: every edit used to reset it to Alpha, a Draft or Production case included).
          lifecycleStage: detail.lifecycleStage ?? 'Alpha',
          category: detail.summary.category && detail.summary.category !== 'U' ? detail.summary.category : undefined,
          turns: (parsed.turns ?? []).map(toTurnRow),
          conversationLevelAssertions: (parsed.conversationLevelAssertions ?? []).map(toConvRow),
        };
        form.setFieldsValue(next);
        opened();
      })
      .catch((err: Error) => {
        if (mine === generation.current) void message.error('Failed to load scenario: ' + err.message);
      })
      .finally(() => {
        if (mine === generation.current) setLoading(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, scenarioId, reload]);

  // ── Unsaved work: every way out asks first ────────────────────────────
  const dirty = open && !loading && (JSON.stringify(values) !== pristine.current
    || (scenarioId == null && tags.length > 0) || draftPrompt.trim().length > 0);
  const requestClose = useUnsavedExit(dirty, scenarioId ? `scenario ${scenarioId}` : 'this new scenario', onClose);

  // ── The domain's categories, for the Category field ───────────────────
  useEffect(() => {
    if (!open || !values.domain) return;
    let cancelled = false;
    listScenarioCategories(values.domain)
      .then((rows) => !cancelled && setCategories(rows))
      .catch(() => !cancelled && setCategories([]));
    return () => {
      cancelled = true;
    };
  }, [open, values.domain]);

  /** Files the new scenario in a category from the coverage list. */
  const fileIn = (code: string) => {
    form.setFieldsValue({ category: code });
    setValues(form.getFieldsValue(true) as EditorFormValues);
  };

  // ── Coverage gaps when agent changes ──────────────────────────────────
  useEffect(() => {
    if (!open || !values.agentId) {
      setCoverage([]);
      return;
    }
    setCoverageLoading(true);
    getCoverageGaps(values.agentId, values.domain)
      .then(setCoverage)
      .catch(() => setCoverage([]))
      .finally(() => setCoverageLoading(false));
  }, [open, values.agentId, values.domain]);

  // ── Similarity (debounced) ────────────────────────────────────────────
  useEffect(() => {
    if (!open || !values.agentId) return;
    const text = `${values.scenarioId} ${values.description} ${values.turns?.[0]?.user ?? ''}`.trim();
    if (text.length < 10) {
      setSimilar([]);
      return;
    }
    if (similarTimer.current) window.clearTimeout(similarTimer.current);
    similarTimer.current = window.setTimeout(() => {
      void getSimilarScenarios(values.agentId!, text, scenarioId ?? undefined, 5)
        .then(setSimilar)
        .catch(() => setSimilar([]));
    }, 600);
    return () => {
      if (similarTimer.current) window.clearTimeout(similarTimer.current);
    };
  }, [open, values.agentId, values.scenarioId, values.description, values.turns, scenarioId]);

  // ── Live JSON preview (canonical shape) ───────────────────────────────
  const canonicalJson = useMemo(() => buildCanonical(values, extras), [values, extras]);

  // ── Lint ──────────────────────────────────────────────────────────────
  const lints = useMemo(() => computeLints(values), [values]);

  // ── Tags (saved on their own) ─────────────────────────────────────────
  const saveTags = async (next: string[]) => {
    if (scenarioId == null) {
      setTags(next);
      return;
    }
    const mine = generation.current;
    const before = tags;
    setTags(next);
    setTagsState('saving');
    try {
      const stored = await setScenarioTags(scenarioId, next);
      onTagsSaved?.();
      if (mine !== generation.current) return; // saved for the scenario it was typed on; the drawer has moved on
      setTags(stored);
      setTagsState('saved');
    } catch (err) {
      if (mine !== generation.current) return;
      setTags(before);
      setTagsState('idle');
      void message.error('Tags not saved: ' + (err as Error).message);
    }
  };

  // ── Removing a turn or rule that holds checks this form cannot show ───
  const removeRow = (hidden: string[], what: 'turn' | 'rule', remove: () => void) => {
    if (hidden.length === 0) {
      remove();
      return;
    }
    Modal.confirm({
      title: `Remove this ${what} and the checks it holds?`,
      content: `It also checks ${hidden.join('; ')}, which this screen does not show. They leave the scenario when you save.`,
      okText: `Remove ${what}`,
      okButtonProps: { danger: true },
      cancelText: 'Keep it',
      onOk: () => {
        setRemovedHiddenChecks(true);
        remove();
      },
    });
  };

  // ── Save ──────────────────────────────────────────────────────────────
  const handleSave = async () => {
    try {
      const v = await form.validateFields();
      if (!v.agentId) {
        void message.error('Agent is required.');
        return;
      }
      setSaving(true);
      // The whole store, not just the fields with a control: each row's `source` holds what this form cannot show.
      const all = form.getFieldsValue(true) as EditorFormValues;
      const out = await upsertScenario({
        domain: v.domain,
        scenarioId: v.scenarioId,
        agentId: v.agentId,
        merchantId: v.merchantId ?? 100,
        memberId: v.memberId ?? 1,
        description: v.description,
        lifecycleStage: v.lifecycleStage,
        // A new scenario sends no version, so an id that exists is refused rather than overwritten.
        baseHash: scenarioId == null ? null : baseHash,
        category: v.category ?? null,
        turns: (all.turns ?? []).map(rowToTurn),
        conversationLevelAssertions: (all.conversationLevelAssertions ?? []).map(rowToConv),
        infusionProtocolId: extras.infusionProtocolId,
        memberContactId: extras.memberContactId,
        acceptRemovedChecks: removedHiddenChecks,
      });
      if (!upsertSaved(out)) {
        const why = out.problems.join(' ') || 'Nothing was saved.';
        if (out.outcome === 'Conflict') setConflict(why);
        else if (out.outcome === 'AlreadyExists') form.setFields([{ name: 'scenarioId', errors: [why] }]);
        else void message.error(why, 10);
        return;
      }
      void message.success(
        out.outcome === 'Inserted'
          ? `Created '${out.scenarioId}' — ready to run.`
          : out.outcome === 'Updated'
          ? `Saved changes to '${out.scenarioId}'.`
          : `No changes to save.`,
      );
      if (scenarioId == null && tags.length > 0) {
        try {
          await setScenarioTags(out.scenarioId, tags);
        } catch (err) {
          void message.warning(`'${out.scenarioId}' is saved, but its tags are not: ${(err as Error).message}`);
        }
      }
      if (onSaved) await onSaved(out.scenarioId);
      onClose();
    } catch (err) {
      const msg = (err as { message?: string })?.message;
      if (msg) void message.error('Save failed: ' + msg, 10);
    } finally {
      setSaving(false);
    }
  };

  // ── AI draft ──────────────────────────────────────────────────────────
  const handleDraft = async () => {
    if (!values.agentId) {
      void message.warning('Pick an agent first — the AI needs the agent context to draft well.');
      return;
    }
    if (draftPrompt.trim().length < 10) {
      void message.warning('Describe the scenario you want in a sentence or two.');
      return;
    }
    setDrafting(true);
    setDraftError(null);
    try {
      const res = await draftScenario({
        agentId: values.agentId,
        userRequest: draftPrompt.trim(),
        scenarioModelId: draftModel?.aIModelId,
      });
      setAnsweredBy(res.answeredBy ?? null);
      if (res.draft) {
        const next: EditorFormValues = {
          ...values,
          scenarioId: res.draft.scenarioId || values.scenarioId,
          description: res.draft.description ?? '',
          merchantId: res.draft.merchantId || values.merchantId,
          memberId: res.draft.memberId || values.memberId,
          turns: (res.draft.turns ?? []).map(toTurnRow),
          conversationLevelAssertions: (res.draft.conversationLevelAssertions ?? []).map(toConvRow),
        };
        form.setFieldsValue(next);
        setValues(next);
      }
      setDraftRationale(res.rationale ?? null);
      if (res.similarScenarioIds?.length) {
        setSimilar(
          res.similarScenarioIds.map((id) => ({
            scenarioId: id,
            description: '(flagged by AI as overlapping)',
            category: 'U',
            score: 1,
          })),
        );
      }
    } catch (err) {
      setDraftError((err as Error).message);
    } finally {
      setDrafting(false);
    }
  };

  // ── Export ────────────────────────────────────────────────────────────
  const handleExport = async () => {
    if (!values.scenarioId) {
      void message.warning('Save the scenario first — export reads the persisted body.');
      return;
    }
    try {
      await exportScenarioJson(values.scenarioId);
    } catch (err) {
      void message.error('Export failed: ' + (err as Error).message);
    }
  };

  return (
    <Drawer
      title={
        <Space size={8}>
          <ExperimentOutlined style={{ color: colors.violet[500] }} />
          <span>{scenarioId ? 'Edit scenario' : 'New scenario'}</span>
        </Space>
      }
      width="92%"
      open={open}
      onClose={requestClose}
      destroyOnHidden
      extra={
        <Space>
          <Button icon={<DownloadOutlined />} onClick={handleExport} disabled={!values.scenarioId}>
            Export JSON
          </Button>
          <Button icon={<CloseOutlined />} onClick={requestClose}>
            Cancel
          </Button>
          <Button type="primary" icon={<SaveOutlined />} loading={saving} disabled={loading} onClick={handleSave}>
            Save scenario
          </Button>
        </Space>
      }
    >
      {loading ? (
        <div style={{ padding: 60, textAlign: 'center' }}>
          <Spin size="large" />
        </div>
      ) : (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'minmax(0, 1.1fr) minmax(0, 0.9fr) 320px',
            gap: 16,
            height: '100%',
          }}
        >
          {/* ── Pane 1: Form ─────────────────────────────────────────── */}
          <div style={{ overflowY: 'auto', paddingRight: 8 }}>
            {conflict && (
              <Alert
                type="warning"
                showIcon
                style={{ marginBottom: 16 }}
                title="Not saved: this scenario changed while you were editing it"
                description={conflict}
                action={
                  <Space direction="vertical">
                    <Button
                      size="small"
                      onClick={() =>
                        Modal.confirm({
                          title: 'Reload the saved version?',
                          content: 'Your edits on this screen are replaced by the version that was saved. Copy anything you want to keep first.',
                          okText: 'Reload',
                          okButtonProps: { danger: true },
                          cancelText: 'Keep editing',
                          onOk: () => setReload((n) => n + 1),
                        })
                      }
                    >
                      Reload their version
                    </Button>
                    <Button size="small" type="text" onClick={() => setConflict(null)}>
                      Keep editing
                    </Button>
                  </Space>
                }
              />
            )}
            <Form
              form={form}
              layout="vertical"
              initialValues={values}
              // The whole store: onValuesChange's own values leave out each row's `source`, and the preview must show
              // what Save will send.
              onValuesChange={() => setValues(form.getFieldsValue(true) as EditorFormValues)}
            >
              <Form.Item
                label="Scenario id"
                name="scenarioId"
                rules={[{ required: true, message: 'scenarioId is required' }]}
                tooltip="Stable, lowercase-hyphenated. Globally unique across all agents."
              >
                <Input placeholder="agent-canonical-name-purpose" />
              </Form.Item>

              <div style={{ marginBottom: 20 }}>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 6 }}>
                  <Text strong>Tags</Text>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {scenarioId == null
                      ? 'Saved with the new scenario.'
                      : tagsState === 'saving'
                        ? 'Saving…'
                        : tagsState === 'saved'
                          ? 'Saved.'
                          : 'Group scenarios in the library, e.g. folfox. Each change saves on its own.'}
                  </Text>
                </div>
                <TagsInput
                  value={tags}
                  onChange={(next) => void saveTags(next)}
                  placeholder="Add a tag, then Enter"
                  size="middle"
                  inputWidth={200}
                  disabled={tagsState === 'saving'}
                />
              </div>

              <Space.Compact block style={{ marginBottom: 16 }}>
                <Form.Item
                  label="Agent"
                  name="agentId"
                  style={{ flex: 2, marginBottom: 0 }}
                  rules={[{ required: true, message: 'Agent is required' }]}
                >
                  <AgentPickerField placeholder="Select agent" />
                </Form.Item>
                <Form.Item
                  label="Domain"
                  name="domain"
                  style={{ flex: 1, marginBottom: 0, marginLeft: 12 }}
                  rules={[{ required: true }]}
                >
                  <Select
                    options={[
                      { value: 'oncology', label: 'oncology' },
                      { value: 'rpm', label: 'rpm' },
                      { value: 'general', label: 'general' },
                    ]}
                  />
                </Form.Item>
                <Form.Item
                  label="Stage"
                  name="lifecycleStage"
                  style={{ flex: 1, marginBottom: 0, marginLeft: 12 }}
                >
                  <Select
                    aria-label="Stage"
                    options={[
                      { value: 'Draft', label: 'Draft (exploratory)' },
                      { value: 'Smoke', label: 'Smoke' },
                      { value: 'Alpha', label: 'Alpha' },
                      { value: 'Beta', label: 'Beta' },
                      { value: 'Production', label: 'Production' },
                    ]}
                  />
                </Form.Item>
              </Space.Compact>

              <Form.Item
                label="Category"
                name="category"
                tooltip="Coverage counts this scenario in the category chosen here, whatever its id says."
                rules={scenarioId == null ? [{ required: true, message: 'Choose the category this scenario covers' }] : []}
              >
                <Select
                  aria-label="Category"
                  allowClear={scenarioId != null}
                  placeholder={scenarioId == null ? 'Choose what this scenario covers' : 'Uncategorized'}
                  options={categories.map((c) => ({ value: c.code, label: `${c.code} · ${c.displayName}` }))}
                  notFoundContent={`No categories are set up for ${values.domain}.`}
                />
              </Form.Item>

              <Space.Compact block style={{ marginBottom: 16 }}>
                <Form.Item label="Merchant id" name="merchantId" style={{ flex: 1, marginBottom: 0 }}>
                  <InputNumber style={{ width: '100%' }} min={1} />
                </Form.Item>
                <Form.Item label="Member id" name="memberId" style={{ flex: 1, marginBottom: 0, marginLeft: 12 }}>
                  <InputNumber style={{ width: '100%' }} min={1} />
                </Form.Item>
              </Space.Compact>
              {(extras.infusionProtocolId !== 0 || extras.memberContactId !== 0) && (
                <Text type="secondary" style={{ display: 'block', fontSize: 12, marginTop: -8, marginBottom: 16 }}>
                  {[
                    extras.infusionProtocolId !== 0 ? `Runs against protocol template #${extras.infusionProtocolId}` : null,
                    extras.memberContactId !== 0 ? `signed in as family member #${extras.memberContactId}` : null,
                  ].filter(Boolean).join(', ')}
                  . Not editable here; kept when you save.
                </Text>
              )}

              <Form.Item
                label="Description"
                name="description"
                tooltip="One or two sentences in plain English explaining what behavior this scenario verifies."
              >
                <TextArea rows={2} placeholder="What this scenario checks and why it matters." />
              </Form.Item>

              <Form.List name="turns">
                {(fields, { add, remove }) => (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <Text strong>Turns ({fields.length})</Text>
                      <Button
                        size="small"
                        icon={<PlusOutlined />}
                        onClick={() => add({ user: '', expect: emptyExpect() })}
                      >
                        Add turn
                      </Button>
                    </div>
                    {fields.map((field, i) => (
                      <TurnEditor
                        key={field.key}
                        index={i}
                        fieldName={field.name}
                        isDark={isDark}
                        onRemove={() =>
                          removeRow(
                            hiddenChecks(form.getFieldValue(['turns', field.name, 'source']), EDITABLE_EXPECT),
                            'turn',
                            () => remove(field.name),
                          )
                        }
                      />
                    ))}
                    {fields.length === 0 && (
                      <Empty
                        description="No turns yet — add one or use the AI co-pilot →"
                        imageStyle={{ height: 60 }}
                      />
                    )}
                  </div>
                )}
              </Form.List>

              <div style={{ marginTop: 24 }}>
                <Text strong>Conversation-level rules</Text>
                <div style={{ fontSize: 12, color: colors.slate[500], marginBottom: 8 }}>
                  Cross-turn assertions — phrases that must (or must never) appear anywhere in the conversation.
                </div>
                <Form.List name="conversationLevelAssertions">
                  {(fields, { add, remove }) => (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                      {fields.map((field, i) => (
                        <ConvRuleEditor
                          key={field.key}
                          index={i}
                          fieldName={field.name}
                          isDark={isDark}
                          onRemove={() =>
                            removeRow(
                              hiddenChecks(
                                form.getFieldValue(['conversationLevelAssertions', field.name, 'source']),
                                EDITABLE_RULE,
                              ),
                              'rule',
                              () => remove(field.name),
                            )
                          }
                        />
                      ))}
                      <Button
                        size="small"
                        icon={<PlusOutlined />}
                        onClick={() =>
                          add({ rule: '', mustNotAppearInAnyTurn: '', mustAppearAtLeastOnce: '' })
                        }
                      >
                        Add rule
                      </Button>
                    </div>
                  )}
                </Form.List>
              </div>
            </Form>
          </div>

          {/* ── Pane 2: Live preview ─────────────────────────────────── */}
          <div
            style={{
              overflowY: 'auto',
              background: isDark ? colors.slate[900] : colors.slate[50],
              border: `1px solid ${isDark ? colors.slate[700] : colors.slate[200]}`,
              borderRadius: 8,
              padding: 12,
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <Text strong style={{ fontSize: 12 }}>
                CANONICAL JSON
              </Text>
              <Text type="secondary" style={{ fontSize: 11 }}>
                Saved to ScenarioIndex + ScenarioSnapshot
              </Text>
            </div>
            <pre
              style={{
                fontFamily: "'JetBrains Mono', monospace",
                fontSize: 11.5,
                lineHeight: 1.5,
                margin: 0,
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                color: isDark ? colors.slate[200] : colors.slate[800],
              }}
            >
              {canonicalJson}
            </pre>
          </div>

          {/* ── Pane 3: Smart sidebar ────────────────────────────────── */}
          <div style={{ overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 12 }}>
            {/* AI co-pilot */}
            <SidebarCard title={<Space><ThunderboltOutlined /> AI co-pilot</Space>} isDark={isDark}>
              <TextArea
                rows={3}
                placeholder='e.g. "patient asks about late-onset diarrhea on day 5 — agent must screen for severity and advise hydration without naming a specific medication"'
                value={draftPrompt}
                onChange={(e) => setDraftPrompt(e.target.value)}
                style={{ fontSize: 13 }}
              />
              <Button
                type="primary"
                icon={<ThunderboltOutlined />}
                loading={drafting}
                onClick={handleDraft}
                disabled={!values.agentId || draftPrompt.trim().length < 10}
                block
                style={{ marginTop: 8 }}
              >
                Draft with AI
              </Button>
              <div style={{ marginTop: 6, fontSize: 12, display: 'flex', alignItems: 'baseline', gap: 4, flexWrap: 'wrap' }}>
                <Text type="secondary" style={{ fontSize: 12 }}>Drafts with</Text>
                <Text strong style={{ fontSize: 12 }}>{modelLine(draftModel ?? scenarioModel) ?? 'no Scenario model'}</Text>
                {draftModel && <Text type="secondary" style={{ fontSize: 12 }}>(this draft only)</Text>}
                <Button
                  type="link"
                  size="small"
                  style={{ padding: 0, height: 'auto', fontSize: 12 }}
                  onClick={() => setDraftModelPickerOpen(true)}
                >
                  Change…
                </Button>
              </div>
              {scenarioModelProblem && !draftModel && (
                <Alert type="warning" showIcon title={scenarioModelProblem} style={{ marginTop: 6, fontSize: 12 }} />
              )}
              {answeredBy && (
                <Text type="secondary" style={{ display: 'block', fontSize: 11.5, marginTop: 4 }}>
                  {`Last draft written by ${answeredBy.deploymentName ?? answeredBy.modelName}${answeredBy.apiTitle ? ` · ${answeredBy.apiTitle}` : ''}`}
                </Text>
              )}
              {draftError && (
                <Alert type="error" showIcon message={draftError} style={{ marginTop: 8 }} closable />
              )}
              {draftRationale && (
                <div
                  style={{
                    marginTop: 8,
                    padding: '8px 10px',
                    background: isDark ? colors.slate[800] : colors.slate[50],
                    border: `1px solid ${isDark ? colors.slate[700] : colors.slate[200]}`,
                    borderRadius: 6,
                    fontSize: 12,
                    lineHeight: 1.5,
                    color: isDark ? colors.slate[300] : colors.slate[700],
                  }}
                >
                  <Text strong style={{ fontSize: 11, textTransform: 'uppercase' }}>
                    Rationale
                  </Text>
                  <div style={{ marginTop: 4 }}>{draftRationale}</div>
                </div>
              )}
            </SidebarCard>

            {/* Similar scenarios */}
            <SidebarCard
              title={
                <Space>
                  <span>Similar scenarios</span>
                  <Tag color={similar.length > 0 ? 'orange' : 'default'}>{similar.length}</Tag>
                </Space>
              }
              isDark={isDark}
            >
              {similar.length === 0 ? (
                <Text type="secondary" style={{ fontSize: 12 }}>
                  No close matches in this agent's library — looks like a fresh angle.
                </Text>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {similar.map((s) => (
                    <div
                      key={s.scenarioId}
                      style={{
                        padding: '6px 8px',
                        background: isDark ? colors.slate[800] : '#FFFFFF',
                        border: `1px solid ${isDark ? colors.slate[700] : colors.slate[200]}`,
                        borderRadius: 6,
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 6 }}>
                        <Text
                          style={{
                            fontFamily: "'JetBrains Mono', monospace",
                            fontSize: 11.5,
                            fontWeight: 600,
                            flex: 1,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {s.scenarioId}
                        </Text>
                        <Tag color={s.score > 0.5 ? 'red' : s.score > 0.3 ? 'orange' : 'default'}>
                          {(s.score * 100).toFixed(0)}%
                        </Tag>
                      </div>
                      {s.description && (
                        <div
                          style={{
                            fontSize: 11.5,
                            color: isDark ? colors.slate[400] : colors.slate[600],
                            marginTop: 2,
                            overflow: 'hidden',
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                          }}
                        >
                          {s.description}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </SidebarCard>

            {/* Coverage gaps */}
            <SidebarCard
              title={
                <Space>
                  <span>Coverage</span>
                  {coverageLoading && <Spin size="small" />}
                </Space>
              }
              isDark={isDark}
            >
              {coverage.length === 0 ? (
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Pick an agent to see category coverage.
                </Text>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  {coverage.map((c) => (
                    <div
                      key={c.code}
                      {...(scenarioId == null
                        ? {
                            role: 'button',
                            tabIndex: 0,
                            title: `File this scenario in ${c.label}`,
                            onClick: () => fileIn(c.code),
                            onKeyDown: (e: React.KeyboardEvent) => (e.key === 'Enter' || e.key === ' ') && fileIn(c.code),
                          }
                        : {})}
                      style={{
                        cursor: scenarioId == null ? 'pointer' : undefined,
                        outline: values.category === c.code ? `1px solid ${colors.violet[500]}` : undefined,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                        padding: '4px 6px',
                        borderRadius: 4,
                        background: c.isGap ? (isDark ? '#3F1414' : '#FEF2F2') : 'transparent',
                      }}
                    >
                      <Tag
                        color={c.isGap ? 'red' : 'default'}
                        style={{ fontFamily: "'JetBrains Mono', monospace", margin: 0, minWidth: 32, textAlign: 'center' }}
                      >
                        {c.code}
                      </Tag>
                      <Text
                        style={{
                          flex: 1,
                          fontSize: 12,
                          color: isDark ? colors.slate[300] : colors.slate[700],
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {c.label}
                      </Text>
                      <Text style={{ fontSize: 11, fontFamily: "'JetBrains Mono', monospace" }}>{c.count}</Text>
                    </div>
                  ))}
                </div>
              )}
            </SidebarCard>

            {/* Lint */}
            <SidebarCard
              title={
                <Space>
                  <span>Lint</span>
                  <Tag color={lints.length === 0 ? 'green' : 'orange'}>{lints.length}</Tag>
                </Space>
              }
              isDark={isDark}
            >
              {lints.length === 0 ? (
                <Space size={6}>
                  <CheckCircleOutlined style={{ color: colors.green[500] }} />
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    No issues — looks runnable.
                  </Text>
                </Space>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {lints.map((l, i) => (
                    <Tooltip key={i} title={l.detail}>
                      <div
                        style={{
                          fontSize: 12,
                          color: isDark ? colors.amber[500] : colors.amber[700],
                          display: 'flex',
                          alignItems: 'flex-start',
                          gap: 6,
                        }}
                      >
                        <WarningOutlined style={{ marginTop: 2 }} />
                        <span>{l.message}</span>
                      </div>
                    </Tooltip>
                  ))}
                </div>
              )}
            </SidebarCard>
          </div>
        </div>
      )}
      <ModelPickerDrawer
        open={draftModelPickerOpen}
        onClose={() => setDraftModelPickerOpen(false)}
        role={EvaluationModelRole.Scenario}
        merchantId={merchantId}
        title="Draft this scenario with"
        selectedId={(draftModel ?? scenarioModel)?.aIModelId ?? null}
        onSelect={(o) => setDraftModel(o && o.aIModelId !== scenarioModel?.aIModelId ? o : null)}
      />
    </Drawer>
  );
});

// ── Sub-components ─────────────────────────────────────────────────────────

function SidebarCard({
  title,
  children,
  isDark,
}: {
  title: React.ReactNode;
  children: React.ReactNode;
  isDark: boolean;
}) {
  return (
    <div
      style={{
        background: isDark ? colors.slate[900] : '#FFFFFF',
        border: `1px solid ${isDark ? colors.slate[700] : colors.slate[200]}`,
        borderRadius: 8,
        padding: 12,
      }}
    >
      <div style={{ marginBottom: 8, fontSize: 12, fontWeight: 600 }}>{title}</div>
      {children}
    </div>
  );
}

function TurnEditor({
  index,
  fieldName,
  isDark,
  onRemove,
}: {
  index: number;
  fieldName: number;
  isDark: boolean;
  onRemove: () => void;
}) {
  const source = Form.useWatch(['turns', fieldName, 'source'], { preserve: true });
  const hidden = hiddenChecks(source, EDITABLE_EXPECT);
  return (
    <div
      style={{
        background: isDark ? colors.slate[900] : '#FFFFFF',
        border: `1px solid ${isDark ? colors.slate[700] : colors.slate[200]}`,
        borderRadius: 8,
        padding: 12,
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <Tag color="violet">Turn {index + 1}</Tag>
        <Button size="small" type="text" danger icon={<DeleteOutlined />} onClick={onRemove}>
          Remove
        </Button>
      </div>
      <Form.Item label="User message" name={[fieldName, 'user']} rules={[{ required: true }]} style={{ marginBottom: 12 }}>
        <TextArea rows={2} placeholder="What the user types this turn." />
      </Form.Item>
      <Tabs
        size="small"
        items={[
          {
            key: 'expect',
            label: 'Expect',
            children: (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <Form.Item
                  label="Skills the router must load"
                  name={[fieldName, 'expect', 'skillsLoaded']}
                  tooltip="Comma-separated skill names. Use names verbatim from the agent's linked skills."
                  style={{ marginBottom: 0 }}
                >
                  <Input placeholder="e.g. severity-screen-emergency, hydration-guidance" />
                </Form.Item>
                <Form.Item
                  label="Reply must contain ANY of"
                  name={[fieldName, 'expect', 'responseMustContainAnyOf']}
                  tooltip="Comma-separated. Passes if at least one phrase appears in the agent's reply."
                  style={{ marginBottom: 0 }}
                >
                  <Input placeholder="e.g. call your team, call your nurse" />
                </Form.Item>
                <Form.Item
                  label="Reply must contain ALL of"
                  name={[fieldName, 'expect', 'responseMustContain']}
                  style={{ marginBottom: 0 }}
                >
                  <Input placeholder="comma-separated phrases" />
                </Form.Item>
                <Form.Item
                  label="Reply must NOT contain"
                  name={[fieldName, 'expect', 'responseMustNotContain']}
                  tooltip="Comma-separated. Use for safety-critical 'never say X' rules."
                  style={{ marginBottom: 0 }}
                >
                  <Input placeholder="e.g. take ibuprofen, take aspirin" />
                </Form.Item>
                <HiddenChecksNote hidden={hidden} />
              </div>
            ),
          },
        ]}
      />
    </div>
  );
}

function ConvRuleEditor({
  index,
  fieldName,
  isDark,
  onRemove,
}: {
  index: number;
  fieldName: number;
  isDark: boolean;
  onRemove: () => void;
}) {
  const source = Form.useWatch(['conversationLevelAssertions', fieldName, 'source'], { preserve: true });
  const hidden = hiddenChecks(source, EDITABLE_RULE);
  return (
    <div
      style={{
        background: isDark ? colors.slate[900] : '#FFFFFF',
        border: `1px solid ${isDark ? colors.slate[700] : colors.slate[200]}`,
        borderRadius: 8,
        padding: 10,
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
        <Tag>Rule {index + 1}</Tag>
        <Button size="small" type="text" danger icon={<DeleteOutlined />} onClick={onRemove} />
      </div>
      <Form.Item label="Rule name" name={[fieldName, 'rule']} style={{ marginBottom: 8 }} rules={[{ required: true }]}>
        <Input placeholder="e.g. No specific medication names" />
      </Form.Item>
      <Form.Item
        label="Must NOT appear in any turn"
        name={[fieldName, 'mustNotAppearInAnyTurn']}
        style={{ marginBottom: 8 }}
      >
        <Input placeholder="comma-separated phrases" />
      </Form.Item>
      <Form.Item
        label="Must appear at least once"
        name={[fieldName, 'mustAppearAtLeastOnce']}
        style={{ marginBottom: 0 }}
      >
        <Input placeholder="comma-separated phrases" />
      </Form.Item>
      <div style={{ marginTop: 8 }}>
        <HiddenChecksNote hidden={hidden} />
      </div>
    </div>
  );
}

/** Tells the person what a turn or rule also checks that this form cannot edit, so a save never surprises them. */
function HiddenChecksNote({ hidden }: { hidden: string[] }) {
  if (hidden.length === 0) return null;
  return (
    <Text type="secondary" style={{ fontSize: 12 }}>
      {`Also checks ${hidden.join('; ')}. Not editable here; kept when you save.`}
    </Text>
  );
}

// ── Helpers ────────────────────────────────────────────────────────────────

/** A turn's or rule's fields as the server sent them: the ones this form shows AND the ones it does not. */
type Loaded = Record<string, unknown>;

interface TurnRow {
  user: string;
  expect: {
    skillsLoaded?: string;
    responseMustContain?: string;
    responseMustContainAnyOf?: string;
    responseMustNotContain?: string;
  };
  /** The expectation as loaded; a save starts from it, so nothing this form cannot show is lost. */
  source?: Loaded | null;
}

interface ConvRow {
  rule: string;
  mustNotAppearInAnyTurn?: string;
  mustAppearAtLeastOnce?: string;
  /** The rule as loaded; a save starts from it. */
  source?: Loaded | null;
}

interface BodyExtras {
  infusionProtocolId: number;
  memberContactId: number;
}

const NO_EXTRAS: BodyExtras = { infusionProtocolId: 0, memberContactId: 0 };

const EDITABLE_EXPECT = ['skillsLoaded', 'responseMustContain', 'responseMustContainAnyOf', 'responseMustNotContain'] as const;
const EDITABLE_RULE = ['rule', 'mustNotAppearInAnyTurn', 'mustAppearAtLeastOnce'] as const;

// How to name a field this form cannot edit. Telling the person only — the server's ScenarioHiddenChecks is the guard.
// A field missing here is still carried through a save and shown by its own name.
const HIDDEN_CHECK_NAMES: Record<string, string> = {
  mustCallTool: 'required tool calls',
  mustNotCallTool: 'forbidden tools',
  mustNotCallToolWith: 'forbidden tool calls',
  expectedClaims: 'expected claims',
  skillsNotLoaded: 'skills that must not load',
  severityScreenComplete: 'the severity screen',
  requireNumbersGroundedInToolOutput: 'that numbers come from tool output',
  groundingTolerance: 'a grounding tolerance',
  groundingIgnorePatterns: 'grounding ignore patterns',
};

/** What a loaded turn or rule checks beyond the fields this form edits, in plain words; empty when nothing. */
function hiddenChecks(source: Loaded | null | undefined, editable: readonly string[]): string[] {
  if (!source) return [];
  return Object.entries(source)
    .filter(([key, value]) => !editable.includes(key) && isSet(value))
    .map(([key, value]) => {
      const name = HIDDEN_CHECK_NAMES[key] ?? key;
      if (!Array.isArray(value)) return name;
      const named = value.map((v) => (v && typeof v === 'object' && 'name' in v ? String((v as { name: unknown }).name) : null)).filter(Boolean);
      return named.length > 0 ? `${name} (${named.join(', ')})` : `${value.length} ${name}`;
    });
}

function isSet(value: unknown): boolean {
  return value !== null && value !== undefined && value !== false && !(Array.isArray(value) && value.length === 0);
}

/** The loaded object without the empty fields the server sends for absent ones. */
function withoutEmpty(source: Loaded | null | undefined): Loaded {
  return Object.fromEntries(Object.entries(source ?? {}).filter(([, value]) => value !== null && value !== undefined));
}

/**
 * A phrase list from its text box: exactly as loaded when the text was not changed — a phrase can hold a comma
 * ("call 911, or go to the ER") and re-splitting would turn it into two — otherwise the text split at commas.
 */
function phrases(text: string | undefined, loaded: unknown): string[] {
  const original = Array.isArray(loaded) ? (loaded as string[]) : [];
  return (text ?? '') === original.join(', ') ? original : splitCsv(text);
}

interface EditorFormValues {
  domain: string;
  scenarioId: string;
  agentId: number | undefined;
  merchantId: number;
  memberId: number;
  description: string;
  lifecycleStage: string;
  /** The category code chosen for this scenario; undefined = none (the id's A1- prefix, if any, still counts). */
  category?: string;
  turns: TurnRow[];
  conversationLevelAssertions: ConvRow[];
}

function blankForm(defaultAgentId?: number, defaultDomain = 'oncology'): EditorFormValues {
  return {
    domain: defaultDomain,
    scenarioId: '',
    agentId: defaultAgentId,
    merchantId: 100,
    memberId: 1,
    description: '',
    lifecycleStage: 'Alpha',
    category: undefined,
    turns: [{ user: '', expect: emptyExpect() }],
    conversationLevelAssertions: [],
  };
}

function emptyExpect() {
  return { skillsLoaded: '', responseMustContain: '', responseMustContainAnyOf: '', responseMustNotContain: '' };
}

function toTurnRow(t: ScenarioTurnSpec): TurnRow {
  return {
    user: t.user ?? '',
    expect: {
      skillsLoaded: (t.expect?.skillsLoaded ?? []).join(', '),
      responseMustContain: (t.expect?.responseMustContain ?? []).join(', '),
      responseMustContainAnyOf: (t.expect?.responseMustContainAnyOf ?? []).join(', '),
      responseMustNotContain: (t.expect?.responseMustNotContain ?? []).join(', '),
    },
    source: (t.expect as Loaded | null | undefined) ?? null,
  };
}

function toConvRow(c: ScenarioConversationAssertion): ConvRow {
  return {
    rule: c.rule ?? '',
    mustNotAppearInAnyTurn: (c.mustNotAppearInAnyTurn ?? []).join(', '),
    mustAppearAtLeastOnce: (c.mustAppearAtLeastOnce ?? []).join(', '),
    source: c as Loaded,
  };
}

// A save starts from what was loaded and lays the edited fields over it. (Note: the Studio client camelCases every key
// it receives, a tool call's argument names included; none was capitalised on 2026-11-01, so they round-trip as written.)
function rowToTurn(r: TurnRow): ScenarioTurnSpec {
  const source = r.source ?? {};
  const expect = withoutEmpty(source);
  for (const key of EDITABLE_EXPECT) {
    const list = phrases(r.expect?.[key], source[key]);
    if (list.length > 0) expect[key] = list;
    else delete expect[key];
  }
  return {
    user: r.user,
    expect: Object.keys(expect).length > 0 ? (expect as ScenarioTurnSpec['expect']) : null,
  };
}

function rowToConv(r: ConvRow): ScenarioConversationAssertion {
  const source = r.source ?? {};
  const rule: Loaded = { ...withoutEmpty(source), rule: r.rule };
  for (const key of ['mustNotAppearInAnyTurn', 'mustAppearAtLeastOnce'] as const) {
    const list = phrases(r[key], source[key]);
    if (list.length > 0) rule[key] = list;
    else delete rule[key];
  }
  return rule as ScenarioConversationAssertion;
}

function splitCsv(s: string | undefined | null): string[] {
  if (!s) return [];
  return s.split(',').map((x) => x.trim()).filter(Boolean);
}

function buildCanonical(v: EditorFormValues, extras: BodyExtras): string {
  const def = {
    scenarioId: v.scenarioId,
    agentId: v.agentId ?? 0,
    merchantId: v.merchantId,
    memberId: v.memberId,
    description: v.description,
    turns: (v.turns ?? []).map(rowToTurn),
    conversationLevelAssertions:
      (v.conversationLevelAssertions ?? []).length > 0
        ? (v.conversationLevelAssertions ?? []).map(rowToConv)
        : undefined,
    infusionProtocolId: extras.infusionProtocolId || undefined,
    memberContactId: extras.memberContactId || undefined,
  };
  // Strip undefined keys recursively for the cleanest preview.
  return JSON.stringify(def, (_k, val) => (val === undefined ? undefined : val), 2);
}

interface Lint {
  message: string;
  detail: string;
}

function computeLints(v: EditorFormValues): Lint[] {
  const out: Lint[] = [];
  if (!v.scenarioId) {
    out.push({ message: 'Scenario id is empty', detail: 'Required for save.' });
  } else if (!/^[a-z0-9][a-z0-9-]*$/.test(v.scenarioId)) {
    out.push({
      message: 'Scenario id should be lowercase-hyphenated',
      detail: 'Use only a-z, 0-9, and hyphens. Start with a letter or digit.',
    });
  }
  if (!v.agentId) out.push({ message: 'No agent selected', detail: 'The validator runs against a specific agent.' });
  if (!v.description || v.description.length < 20) {
    out.push({
      message: 'Description is short',
      detail: 'Describe what behavior this scenario verifies in 1–2 sentences. Helps reviewers and reports.',
    });
  }
  if (!v.turns || v.turns.length === 0) {
    out.push({ message: 'No turns', detail: 'At least one user turn is required.' });
  }
  v.turns?.forEach((t, i) => {
    if (!t.user || t.user.trim().length < 4) {
      out.push({ message: `Turn ${i + 1}: user message empty`, detail: 'Every turn needs a user message.' });
    }
    const e = t.expect ?? {};
    const hasAny =
      splitCsv(e.skillsLoaded).length +
        splitCsv(e.responseMustContain).length +
        splitCsv(e.responseMustContainAnyOf).length +
        splitCsv(e.responseMustNotContain).length >
      0;
    if (i === 0 && !hasAny) {
      out.push({
        message: `Turn 1 has no assertions`,
        detail:
          'A scenario with zero assertions can pass even when the agent breaks. Add at least one expectation.',
      });
    }
  });
  return out;
}

/** The scenario's agent, chosen in the agent drawer every other Studio agent choice uses. Form.Item supplies value/onChange. */
function AgentPickerField({
  value,
  onChange,
  placeholder,
}: {
  value?: number;
  onChange?: (agentId: number | undefined) => void;
  placeholder?: string;
}) {
  return (
    <AgentPickerTrigger
      value={value ?? null}
      onChange={(id) => onChange?.(id ?? undefined)}
      placeholder={placeholder}
      drawerTitle="Pick the scenario's agent"
      allowClear={false}
      style={{ width: '100%' }}
    />
  );
}
